// src/pages/FraudLogs.js
// Fraud Management → Fraud Logs investigation page.
// Assembles: Sidebar + Navbar + FilterBar + Table + Drawer.

import React from 'react';
import { Container } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import DashboardNavbar from '../components/DashboardNavbar';
import FraudLogsFilter from '../components/fraud/FraudLogsFilter';
import FraudLogsTable from '../components/fraud/FraudLogsTable';
import FraudLogDrawer from '../components/fraud/FraudLogDrawer';
import useFraudLogs from '../hooks/useFraudLogs';

// ─── Index-error notice ───────────────────────────────────────────────────────
const IndexNotice = ({ message }) => (
  <div
    style={{
      background: 'rgba(245,158,11,0.10)',
      border: '1px solid rgba(245,158,11,0.3)',
      borderRadius: 8,
      padding: '10px 16px',
      marginBottom: 16,
      fontSize: 12,
      color: '#F59E0B',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
    }}
  >
    <span style={{ flexShrink: 0, fontSize: 14 }}>⚠️</span>
    <span>
      <strong>Composite index required.</strong>{' '}
      {message}{' '}
      Visit the{' '}
      <a
        href="https://console.firebase.google.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#F59E0B' }}
      >
        Firebase Console
      </a>{' '}
      to create the index, then re-apply your filters.
    </span>
  </div>
);

// ─── General error notice ──────────────────────────────────────────────────────
const ErrorBanner = ({ message }) => (
  <div
    style={{
      background: 'rgba(239,68,68,0.10)',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8,
      padding: '10px 16px',
      marginBottom: 16,
      fontSize: 12,
      color: '#EF4444',
    }}
  >
    ⚠️ {message}
  </div>
);

// ─── Page ────────────────────────────────────────────────────────────────────
const FraudLogs = () => {
  const {
    filters,
    setFilters,
    applyFilters,
    resetFilters,
    logs,
    loading,
    error,
    indexError,
    page,
    hasMore,
    goNext,
    goPrev,
    selectedLog,
    drawerOpen,
    openDrawer,
    closeDrawer,
  } = useFraudLogs();

  return (
    <div className="d-flex entire-bg-color">
      <Sidebar />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <DashboardNavbar />

        {/* ── Top spacer (matches navbar height) ── */}
        <div style={{ marginTop: '70px' }} />

        <section className="py-4 nav-body-bg btm-rad">
          <Container fluid className="px-4">

            {/* ── Page heading ── */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="text-h2 mb-0">Fraud Logs</h3>
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--divider)',
                  borderRadius: 20,
                  padding: '3px 12px',
                }}
              >
                {loading ? 'Loading…' : `${logs.length} records on page ${page}`}
              </span>
            </div>

            {/* ── Filter bar ── */}
            <FraudLogsFilter
              filters={filters}
              setFilters={setFilters}
              onApply={applyFilters}
              onReset={resetFilters}
              loading={loading}
            />

            {/* ── Notices ── */}
            {indexError && <IndexNotice message={error} />}
            {error && !indexError && <ErrorBanner message={error} />}

            {/* ── Table ── */}
            <FraudLogsTable
              logs={logs}
              loading={loading}
              page={page}
              hasMore={hasMore}
              onPrev={goPrev}
              onNext={goNext}
              onRowClick={openDrawer}
            />

          </Container>
        </section>

        <div style={{ height: 40 }} />
      </div>

      {/* ── Detail drawer (teleported to body level via fixed position) ── */}
      <FraudLogDrawer
        log={selectedLog}
        open={drawerOpen}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default FraudLogs;
